// Copyright (c) 2022, 37.78 Tecnologia Ltda. All rights reserved.

import React from "react";

import { Box, Grid, Paper, styled, Typography } from "@3778.care/goban";
import PropTypes from "prop-types";

import { useMixpanel } from "../../contexts/mixpanel";
import {
  buildWhatsappLink,
  isFastContact,
} from "../../domain/insurancePlan/utils";
import { FastContactHomeIcon } from "./components/ContatoRapido/FastContactHomeIcon";

const StyledLink = styled("a")(() => ({
  textDecoration: "none",
}));

const GridContainer = styled(Grid)(({ theme: { spacing } }) => ({
  width: `calc(100% + ${spacing(1.5)}px)`,
  margin: -spacing(0.75),
}));

const GridItem = styled(Grid)(({ theme: { spacing } }) => ({
  padding: spacing(0.2, 0.4, 0.2, 0.2),
}));

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) =>
    prop !== "isFastContact" && prop !== "isParentHome",
})(({ isFastContact, isParentHome, theme: { palette, spacing } }) => ({
  cursor: "pointer",
  height: "100%",
  width: "100%",
  ...(isFastContact && {
    backgroundColor: palette.primary.light,
    padding: spacing(1, 0.7),
  }),
  ...(isParentHome &&
    !isFastContact && {
      padding: spacing(2),
    }),
  ...(!isParentHome &&
    !isFastContact && {
      padding: spacing(2, 3),
    }),
}));

export const ServiceCardWhatsapp = ({
  insurancePlan: {
    serviceIdentifier,
    service,
    healthcareService,
    comment: customComment,
  },
  parent,
}) => {
  const isFastContactCard = isFastContact(service);
  const isParentHome = parent === "home";
  const whatsappLink = buildWhatsappLink(JSON.parse(serviceIdentifier));
  const { track } = useMixpanel();

  const handleClick = () => {
    track(`Open service - ${service}`);
  };

  return (
    <StyledLink href={whatsappLink} target="_blank" rel="noreferrer">
      <StyledPaper
        data-cy="service-card"
        elevation={0}
        id={serviceIdentifier}
        isFastContact={isFastContactCard}
        isParentHome={isParentHome}
        onClick={handleClick}
        style={{ boxShadow: "none" }}
        variant={isFastContactCard ? "elevation" : "outlined"}
      >
        <GridContainer alignItems="center" container spacing={0}>
          <GridItem item xs={!isFastContactCard && 12}>
            <Box display="flex">
              <FastContactHomeIcon />
            </Box>
          </GridItem>
          <Grid item xs={9}>
            <Typography className="ServiceCardWhatsapp" variant="subtitle2">
              {healthcareService.name}
            </Typography>
            {parent === "services" && (
              <Typography
                className="ServiceCardWhatsapp"
                component="h4"
                variant="caption"
              >
                {customComment ?? healthcareService.comment}
              </Typography>
            )}
          </Grid>
        </GridContainer>
      </StyledPaper>
    </StyledLink>
  );
};

ServiceCardWhatsapp.propTypes = {
  insurancePlan: PropTypes.object,
  parent: PropTypes.string,
};
